import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { BypassOtpBanner } from "../components/BypassOtpBanner";
import { OtpChannelPicker } from "../components/OtpChannelPicker";
import { OtpInput } from "../components/OtpInput";
import { ApiError } from "../lib/apiFetch";
import { otpSentMessage } from "../lib/otpCopy";
import type {
  OtpChannel,
  PartnerSignupResendRequest,
  PartnerSignupVerifyRequest,
  PartnerSignupVerifyResponse,
} from "../types/api";
import { AuthCard } from "./AuthCard";
import { clearSignupPassword, getSignupPassword } from "./signupHandoff";
import { usePortalApi } from "./usePortalApi";
import { usePortalAuthConfig } from "./usePortalAuthConfig";
import { usePortalSession } from "./usePortalSession";

/** Seconds the send button stays disabled; mirrors the server's `SEND_COOLDOWN_SECONDS`. */
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * State carried into `/verify-phone` from the set-password submit.
 *
 * `challenge` is the opaque token the set-password response returned, which
 * verify and send both need. `code` is the set-password link code, so the back
 * link can return to `/set-password?code=…`, where resubmitting starts a fresh
 * challenge. `bypassSmsOtp` comes from the same response and drives the bypass
 * banner; missing and false mean the same. The password is not carried here:
 * it is stashed in the in-memory `signupHandoff`, so it never lands in
 * `history.state` (see the redirect note below).
 */
interface VerifyPhoneState {
  challenge: string;
  code: string;
  bypassSmsOtp: boolean;
}

/**
 * Narrow the untyped `useLocation().state` (`unknown`) to our state shape.
 * Returns null when the shape doesn't match. A runtime guard, not an `as`-cast.
 */
function readState(state: unknown): VerifyPhoneState | null {
  if (typeof state !== "object" || state === null) return null;
  const s = state as Record<string, unknown>;
  if (typeof s.challenge !== "string" || s.challenge.length === 0) return null;
  if (typeof s.code !== "string") return null;
  return { challenge: s.challenge, code: s.code, bypassSmsOtp: s.bypassSmsOtp === true };
}

/**
 * OTP entry for first-time portal account setup (`/verify-phone`).
 *
 * Set-password, for a first-time onboard or invite link, mints a challenge for
 * the entered phone and routes here with `{ challenge, code }` in router state,
 * stashing the password in the in-memory `signupHandoff`; no code is sent at
 * that step. The user picks a delivery channel (text message or voice call,
 * text preselected) and presses Send code. `<apiPrefix>/auth/signup/resend` is
 * the only send path, for the first send and every resend, and targets the
 * phone carried inside the challenge, never a client-supplied one. The account
 * is created only when `POST <apiPrefix>/auth/signup/verify` accepts the OTP.
 *
 * - The code is 6 digits. Verify auto-submits once 6 digits are entered, and a
 *   busy guard blocks a double fire. The input appears only after a send or a
 *   429 on Send, since a still-valid code from the cooldown window can be typed
 *   directly.
 * - `session` on the verify response: store it and navigate to `/`.
 * - reason `"rejected"` or `"expired"`: show the matching error and clear the
 *   input.
 * - reason `"max_attempts_exceeded"`: restart at `/set-password?code=…`, where
 *   resubmitting the form starts a fresh challenge.
 * - reason `"phone_in_use"`: show the error and stay, so the user can go back
 *   and enter another number.
 * - a 400 (a dead challenge): the dead-challenge state with a link back to
 *   set-password. Any other failure shows a generic error.
 *
 * The cooldown starts at 0, since nothing is sent on arrival, and runs from the
 * first successful send; a 429 restarts it. The server enforces the real
 * per-phone cooldown, one window shared across both channels.
 *
 * Hard-refresh handling: React Router v7 persists `location.state` into
 * `history.state` and rehydrates it on reload, so router state alone can't tell
 * a fresh navigation from a reload. The password lives only in the in-memory
 * `signupHandoff`, which a reload wipes, so a null stashed password with
 * rehydrated router state signals a reload. Missing router state or a null
 * password redirects to `/set-password?code=…` when the code survives, else to
 * `/login`, rather than resuming a broken form.
 */
export function VerifyPhone(): ReactNode {
  const api = usePortalApi();
  const { apiPrefix } = usePortalAuthConfig();
  const { setSession } = usePortalSession();
  const navigate = useNavigate();
  const location = useLocation();
  const carried = readState(location.state);
  const password = getSignupPassword();

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deadChallenge, setDeadChallenge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  // `channel` is the delivery choice for the next send; the picker stays
  // visible after sending. `sentVia` is the channel of the last successful
  // send, null until one goes out, and drives the subtitle and button label.
  const [channel, setChannel] = useState<OtpChannel>("sms");
  const [sentVia, setSentVia] = useState<OtpChannel | null>(null);
  // True once a code is probably out there; gates the code input. Set on a
  // successful send and on a 429, since a code from the cooldown window may
  // still be valid. `sentVia` stays null on a 429 so the copy never claims a send.
  const [sent, setSent] = useState(false);
  // 0 on arrival, because set-password sends no code.
  const [cooldown, setCooldown] = useState(0);

  // The countdown synchronizes with wall-clock time, so it is an effect with an
  // interval. It re-arms whenever a send or a 429 raises `cooldown` again.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Guard the verify request against a double-fire (e.g. a paste that fills 6
  // digits while a previous request is still in flight). Mirrors `busy` but is
  // synchronous so two onChange ticks in the same frame can't both pass.
  const inFlight = useRef(false);

  // Set true the moment a signup succeeds, before `setSession`. The success
  // branch then clears the stashed password, and `setSession` re-renders this
  // still-matched route, where `password === null` would otherwise make the
  // redirect guard below fire `<Navigate to="/set-password">` and override the
  // imperative `navigate("/")`. A ref doesn't itself re-render, and it is
  // already true when the setSession re-render reads it. Render nothing on that
  // transient frame while the route changes.
  const succeeded = useRef(false);
  if (succeeded.current) return null;

  // No router state, or a reload wiped the in-memory password hand-off: rather
  // than render a broken form, send the user back to start a fresh challenge.
  // The carried `code`, or a best-effort read of the rehydrated state, targets
  // the set-password form so they can re-enter the phone.
  if (!carried || password === null) {
    const fallbackCode = carried?.code ?? readCode(location.state);
    return (
      <Navigate
        to={fallbackCode ? `/set-password?code=${encodeURIComponent(fallbackCode)}` : "/login"}
        replace
      />
    );
  }

  const { challenge, code: codeFromLink, bypassSmsOtp } = carried;
  // Past the guard, `password` is non-null. Capture it in a local `const` so TS
  // carries the narrowing into the `verify` closure (it doesn't narrow the outer
  // binding inside a nested function defined after the guard).
  const verifiedPassword: string = password;

  function verify(otp: string): void {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const body: PartnerSignupVerifyRequest = { challenge, code: otp, password: verifiedPassword };
    api<PartnerSignupVerifyResponse>(`${apiPrefix}/auth/signup/verify`, {
      method: "POST",
      body: body as unknown as Record<string, unknown>,
    })
      .then((res) => {
        if (res.session) {
          // Mark success before setSession, so the re-render it triggers can't
          // let the redirect guard fire (clearSignupPassword nulls the password).
          succeeded.current = true;
          setSession(res.session);
          // Signup succeeded: drop the stashed password so it doesn't linger in
          // memory.
          clearSignupPassword();
          void navigate("/", { replace: true });
          return;
        }
        // OTP not accepted: map the reason to a message or action.
        inFlight.current = false;
        setBusy(false);
        if (res.reason === "rejected") {
          setError("That code didn't match — check the newest code you received and try again.");
          setCode("");
          return;
        }
        if (res.reason === "expired") {
          setError("That code has expired — press “Resend code” below to get a new one.");
          setCode("");
          return;
        }
        if (res.reason === "max_attempts_exceeded") {
          // Too many tries: restart, since resubmitting the form starts a fresh
          // challenge.
          void navigate(`/set-password?code=${encodeURIComponent(codeFromLink)}`, {
            replace: true,
          });
          return;
        }
        if (res.reason === "phone_in_use") {
          // The OTP was accepted, but the server's phone-uniqueness check
          // rejected this number because another account holds it. No account
          // is created and the link isn't burned. Retrying the code is futile,
          // since an accepted OTP can't be replayed; the recovery is the "Go
          // back" link below, to enter a different number. There is no
          // auto-navigation, which would drop this message before it is read.
          setError(
            "This phone number is already registered to another account. Go back and use a different number, or contact support.",
          );
          setCode("");
          return;
        }
        setError("Something went wrong. Please try again.");
        setCode("");
      })
      .catch((err: unknown) => {
        inFlight.current = false;
        setBusy(false);
        // A dead or invalid challenge (expired, or its link already used) 400s,
        // and the user must restart from set-password for a new challenge.
        if (err instanceof ApiError && err.status === 400) {
          setDeadChallenge(true);
          return;
        }
        setError("Something went wrong. Please try again.");
        setCode("");
      });
  }

  function send(): void {
    if (sending || cooldown > 0) return;
    setSending(true);
    setError(null);
    const body: PartnerSignupResendRequest = { challenge, channel };
    api(`${apiPrefix}/auth/signup/resend`, {
      method: "POST",
      body: body as unknown as Record<string, unknown>,
    })
      .then(() => {
        setSending(false);
        setSent(true);
        setSentVia(channel);
        setCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch((err: unknown) => {
        setSending(false);
        // A 429 means the server is still cooling down, and a code from that
        // window is still valid, so reveal the input. Restart the local
        // countdown without claiming a send.
        if (err instanceof ApiError && err.status === 429) {
          setSent(true);
          setCooldown(RESEND_COOLDOWN_SECONDS);
          return;
        }
        if (err instanceof ApiError && err.status === 400) {
          setDeadChallenge(true);
          return;
        }
        setError("We couldn't send the code. Press “Send code” to try again.");
      });
  }

  if (deadChallenge) {
    return (
      <AuthCard
        title="Verify your phone"
        footer={
          <Link
            to={`/set-password?code=${encodeURIComponent(codeFromLink)}`}
            className="text-primary-text hover:text-ember-dark font-medium"
          >
            Back to set up your account
          </Link>
        }
      >
        <Alert
          tone="error"
          title="This verification has expired. Please go back and set up your account again to get a new code."
        />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Verify your phone"
      subtitle={
        sentVia
          ? `${otpSentMessage(sentVia)} Enter it below to finish setting up your account.`
          : "Choose how to get your 6-digit code, then enter it below to finish setting up your account."
      }
      footer={
        <Link
          to={`/set-password?code=${encodeURIComponent(codeFromLink)}`}
          className="text-primary-text hover:text-ember-dark font-medium"
        >
          Wrong number? Go back
        </Link>
      }
    >
      <div className="space-y-5">
        <BypassOtpBanner bypassSmsOtp={bypassSmsOtp} />

        <OtpChannelPicker value={channel} onChange={setChannel} disabled={sending} />

        {sent && (
          <OtpInput
            value={code}
            onChange={(v) => {
              setCode(v);
              if (error) setError(null);
            }}
            onComplete={verify}
            disabled={busy}
            error={error !== null}
          />
        )}

        {error && <Alert tone="error" title={error} />}

        <Button
          type="button"
          variant={sentVia ? "secondary" : "primary"}
          size="lg"
          className="w-full"
          disabled={sending || cooldown > 0}
          onClick={send}
        >
          {sending
            ? "Sending…"
            : cooldown > 0
              ? `${sentVia ? "Resend" : "Send"} code in ${cooldown.toString()}s`
              : sentVia
                ? "Resend code"
                : "Send code"}
        </Button>
      </div>
    </AuthCard>
  );
}

/** Best-effort code extraction from the (untyped) state for the hard-refresh fallback. */
function readCode(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const s = state as Record<string, unknown>;
  return typeof s.code === "string" && s.code.length > 0 ? s.code : null;
}
